import { z } from "zod";
import { actionEffectSchema, modelIdSchema, providerIdSchema, uuidSchema } from "./aiTypes.ts";
import { sha256Schema } from "./changesets.ts";
import { event, request } from "./defs.ts";
import { projectIdSchema } from "./schemas.ts";

/**
 * AI command execution (milestone M2C). The model can only REQUEST a structured command (executable + argv + a
 * workspace-relative cwd). The agent classifies it (allow | require_approval | deny) and runs it with
 * ScopedCommandRunner (shell:false, scrubbed env, caps). Commands that need approval are frozen with a hash and run
 * only after a human approves exactly that frozen request. AI commands never touch the human terminal (PTY).
 */
export const COMMAND_LIMITS = {
  maxArgs: 64,
  maxArgChars: 4096,
  maxCwdChars: 1024,
  defaultTimeoutMs: 120_000,
  maxTimeoutMs: 600_000,
  /** Output kept for display in the browser (per stream). */
  displayOutputChars: 16_000,
} as const;

/** Bare program name (resolved via PATH) or a workspace-relative path such as "./gradlew". */
export const executableSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(
    /^(\.\/)?[A-Za-z0-9._+-]+(\/[A-Za-z0-9._+-]+)*$/,
    "executable must be a program name or ./relative path",
  );
export const argvSchema = z
  .array(
    z
      .string()
      .max(COMMAND_LIMITS.maxArgChars)
      .refine((a) => !a.includes("\0"), "NUL in argument"),
  )
  .max(COMMAND_LIMITS.maxArgs);
export const commandCwdSchema = z.string().max(COMMAND_LIMITS.maxCwdChars);
export const timeoutSchema = z.number().int().min(1000).max(COMMAND_LIMITS.maxTimeoutMs);

export const COMMAND_STATUSES = [
  "pending_approval",
  "running",
  "completed",
  "failed",
  "timed_out",
  "cancelled",
  "denied",
  "expired",
] as const;
export const commandStatusSchema = z.enum(COMMAND_STATUSES);
export type CommandStatus = z.infer<typeof commandStatusSchema>;

export const commandPolicySchema = z.enum(["allow", "require_approval"]);

/** Canonical text the command hash is computed over (machine, workspace, executable, argv, cwd, timeout). */
export function commandHashInput(c: {
  machineId: string;
  projectId: string;
  executable: string;
  args: readonly string[];
  cwd: string;
  timeoutMs: number;
}): string {
  return JSON.stringify([
    "racler-command:v1",
    c.machineId,
    c.projectId,
    c.executable,
    c.args,
    c.cwd,
    c.timeoutMs,
  ]);
}

const resultFields = {
  exitCode: z.number().int().nullable(),
  signal: z.string().max(32).nullable(),
  durationMs: z.number().int().nonnegative(),
  timedOut: z.boolean(),
  cancelled: z.boolean(),
  stdout: z.string().max(COMMAND_LIMITS.displayOutputChars + 200),
  stderr: z.string().max(COMMAND_LIMITS.displayOutputChars + 200),
  stdoutTruncated: z.boolean(),
  stderrTruncated: z.boolean(),
};
export const commandResultSchema = z.strictObject(resultFields);
export type CommandResultInfo = z.infer<typeof commandResultSchema>;

const proposalFields = {
  commandId: uuidSchema,
  executable: executableSchema,
  args: argvSchema,
  cwd: commandCwdSchema,
  timeoutMs: timeoutSchema,
  reason: z.string().max(500).nullable(),
  policy: commandPolicySchema,
  category: z.string().max(64),
  policyReason: z.string().max(300),
  effect: actionEffectSchema.optional(),
};

export const commandInfoSchema = z.strictObject({
  id: uuidSchema,
  sessionId: uuidSchema,
  runId: uuidSchema,
  projectId: projectIdSchema,
  provider: providerIdSchema,
  model: modelIdSchema,
  executable: executableSchema,
  args: argvSchema,
  cwd: commandCwdSchema,
  timeoutMs: timeoutSchema,
  reason: z.string().max(500).nullable(),
  policy: commandPolicySchema,
  category: z.string().max(64),
  policyReason: z.string().max(300),
  effect: actionEffectSchema.optional(),
  status: commandStatusSchema,
  contentHash: sha256Schema,
  result: commandResultSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CommandInfo = z.infer<typeof commandInfoSchema>;

const runIds = { runId: uuidSchema, sessionId: uuidSchema };

/** Files a command changed in the workspace (size/mtime snapshot before vs. after). Never contents. */
const changedFields = {
  changedFiles: z.array(z.string().max(1024)).max(50),
  changedFilesTruncated: z.boolean(),
};

/** Agent → server. The server persists them and re-publishes `command.status` to the owner's browsers. */
export const commandAgentEvents = {
  /** A command the model requested, already classified and (if approval is needed) frozen. */
  "agent/command.proposed": event(
    "agent",
    "command.proposed",
    z.strictObject({ ...runIds, ...proposalFields, contentHash: sha256Schema }),
  ),
  "agent/command.started": event(
    "agent",
    "command.started",
    z.strictObject({ ...runIds, commandId: uuidSchema }),
  ),
  "agent/command.finished": event(
    "agent",
    "command.finished",
    z.strictObject({
      ...runIds,
      commandId: uuidSchema,
      status: z.enum(["completed", "failed", "timed_out", "cancelled"]),
      result: commandResultSchema,
      ...changedFields,
      error: z.string().max(300).nullable(),
    }),
  ),
} as const;

export type CommandAgentEvent = {
  [K in keyof typeof commandAgentEvents]: {
    type: K;
    payload: z.infer<(typeof commandAgentEvents)[K]["payload"]>;
  };
}[keyof typeof commandAgentEvents];

export function parseCommandAgentEvent(msg: {
  channel: string;
  type: string;
  payload: unknown;
}): CommandAgentEvent | null {
  const key = `${msg.channel}/${msg.type}`;
  if (!Object.prototype.hasOwnProperty.call(commandAgentEvents, key)) return null;
  const def = commandAgentEvents[key as keyof typeof commandAgentEvents];
  const parsed = def.payload.safeParse(msg.payload);
  return parsed.success ? ({ type: key, payload: parsed.data } as CommandAgentEvent) : null;
}

/** Full persisted command, as the browser sees it (server → browser `command.status`, `chat.session`). */
export const commandViewSchema = commandInfoSchema.extend({
  changedFiles: z.array(z.string().max(1024)).max(50),
  changedFilesTruncated: z.boolean(),
  error: z.string().max(300).nullable(),
});
export type CommandView = z.infer<typeof commandViewSchema>;

export function parseCommandStatusEvent(msg: {
  channel: string;
  type: string;
  payload: unknown;
}): CommandView | null {
  if (msg.channel !== "agent" || msg.type !== "command.status") return null;
  const parsed = z.strictObject({ command: commandViewSchema }).safeParse(msg.payload);
  return parsed.success ? parsed.data.command : null;
}

const commandIdOnly = z.strictObject({ commandId: uuidSchema });

/** Browser → server. Approve = "run THIS frozen command": the hash the user reviewed must match. */
export const commandRequests = {
  "agent/command.approve": request(
    "agent",
    "command.approve",
    z.strictObject({ commandId: uuidSchema, contentHash: sha256Schema }),
    z.strictObject({ command: commandViewSchema }),
  ),
  "agent/command.deny": request(
    "agent",
    "command.deny",
    commandIdOnly,
    z.strictObject({ command: commandViewSchema }),
  ),
  /** Stop a running command (kills its process tree on the agent). */
  "agent/command.cancel": request(
    "agent",
    "command.cancel",
    commandIdOnly,
    z.strictObject({ cancelled: z.boolean() }),
  ),
} as const;

/**
 * How a paused Agent run continues (inside `ai.run`). The agent verifies everything against what IT froze:
 *  - command: the frozen command must exist, still be pending, and hash to `contentHash`; only then does it run;
 *  - changeset: the agent reads the apply outcome from its own ChangeSet store (the server only says "decided").
 */
export const resumeSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("command"),
    commandId: uuidSchema,
    contentHash: sha256Schema,
    decision: z.enum(["approved", "denied"]),
  }),
  z.strictObject({ kind: z.literal("changeset"), changeSetId: uuidSchema }),
]);
export type Resume = z.infer<typeof resumeSchema>;

/**
 * Server → agent ONLY (never routable from a browser). Approved/denied decisions are NOT sent here: they travel inside
 * the continuation `ai.run` (`resume`), so execution always happens in a run whose output goes back to the model.
 */
export const commandCommands = {
  "agent/command.cancel": request(
    "agent",
    "command.cancel",
    z.strictObject({ commandId: uuidSchema }),
    z.strictObject({ cancelled: z.boolean() }),
  ),
} as const;
